/**
 * System feature flags — the entity shapes behind the registry-backed
 * `/internal/config/flags` endpoints.
 *
 * A "system flag" is one `SystemConfiguration` row addressed by a stable PUBLIC
 * NAME rather than by its raw `(category, key)` coordinates. The backend keeps a
 * REGISTRY mapping each public name to its category, key, value schema and
 * description; only registered names are readable or writable. That registry is
 * the allowlist and the type boundary: without it, a typo in `key` silently
 * creates a dead row that reads back to the operator as "I turned it on", and a
 * malformed `value` is only as safe as the individual resolver's fail-closed
 * discipline.
 *
 * Storage, per flag:
 *   `value`    — the functional payload the resolver reads (flag-specific shape)
 *   `metadata` — `{ reason, changedBy, changedAt }`, the audit trail
 *
 * SCOPE. `school_id` absent → the FLEET-WIDE row (`school_id IS NULL`); present
 * → that school's override row. Reads deep-merge the school row OVER the
 * fleet-wide row per key (`getConfiguration` semantics), so a school row that
 * sets only some keys still inherits the rest from the fleet-wide row.
 */

import { z } from 'zod'

/**
 * Audit record for a single flag write, stored in `SystemConfiguration.metadata`.
 */
export const SystemFlagAuditSchema = z.object({
  reason: z.string().nullable().describe('Free-text reason supplied by the caller, if any'),
  changedBy: z.string().describe("Caller identity (defaults to 'internal-api')"),
  changedAt: z.string().describe('ISO timestamp of the write')
})

export type SystemFlagAudit = z.infer<typeof SystemFlagAuditSchema>

/**
 * The two audit inputs every flag write accepts, as a spreadable field bag.
 * Both are optional — `changedBy` defaults to `'internal-api'` server-side.
 */
export const SystemFlagAuditRequestFields = {
  reason: z.string().max(2000).optional().describe('Optional audit reason for the change'),
  changed_by: z.string().max(200).optional().describe('Optional caller identity for the audit trail')
}

/**
 * Which configuration level(s) supplied the effective value.
 *  - `school` — only a school-scoped row exists
 *  - `system` — only the fleet-wide (`school_id IS NULL`) row exists
 *  - `merged` — BOTH exist; the school row's keys win per key, the rest are
 *               inherited from the fleet-wide row
 *  - `none`   — no row at either level; the flag is at its resolver default
 *               (fail-closed for every flag registered so far)
 */
export const SystemFlagSourceLevelSchema = z.enum(['school', 'system', 'merged', 'none'])

export type SystemFlagSourceLevel = z.infer<typeof SystemFlagSourceLevelSchema>

/**
 * One registry entry as exposed to an operator — enough to discover what is
 * settable, and what shape its value takes, without reading backend source.
 */
export const SystemFlagDescriptorSchema = z.object({
  name: z.string().describe('Stable public flag name — what the endpoints address'),
  category: z.string().describe('`SystemConfiguration.category` this flag is stored under'),
  key: z.string().describe('`SystemConfiguration.key` this flag is stored under'),
  description: z.string().describe('What the flag gates, in one or two sentences'),
  valueKeys: z.array(z.string()).describe('Top-level keys the value object accepts, in registry order')
})

export type SystemFlagDescriptor = z.infer<typeof SystemFlagDescriptorSchema>

/**
 * The resolved state of ONE flag, for an optional school.
 *
 * `effective` is the value a resolver would actually see — the school row deep-
 * merged over the fleet-wide row (or the fleet-wide row alone when no school was
 * requested). `null` means no row exists at either level, i.e. the resolver falls
 * back to its own default.
 */
export const SystemFlagStateSchema = z.object({
  name: z.string().describe('The registered flag name that was read'),
  category: z.string().describe('`SystemConfiguration.category` the value was read from'),
  key: z.string().describe('`SystemConfiguration.key` the value was read from'),
  schoolId: z.string().nullable().describe("School's public ID, or null when the read was fleet-wide"),
  effective: z
    .unknown()
    .nullable()
    .describe('Effective value after the school-over-system deep merge; null when no row exists at either level'),
  effectiveValid: z
    .boolean()
    .describe(
      "Does `effective` still satisfy the registry's value schema? False flags a hand-edited or stale row that a " +
        'fail-closed resolver will silently ignore'
    ),
  systemValue: z.unknown().nullable().describe('Raw fleet-wide row value, or null when no fleet-wide row exists'),
  schoolValue: z
    .unknown()
    .nullable()
    .describe('Raw school-scoped row value, or null when no school row exists (or none was requested)'),
  source: SystemFlagSourceLevelSchema.describe('Which level(s) supplied the effective value'),
  audit: SystemFlagAuditSchema.nullable().describe(
    'Audit record from the most specific level present (school over system), or null if never written through this API'
  )
})

export type SystemFlagState = z.infer<typeof SystemFlagStateSchema>

/**
 * Value shape of the `parent_membership_reconcile` flag — the rollout ladder for
 * the fleet-wide parent-to-section membership reconciler (SCH-860).
 *
 * The three keys are a deliberate ladder, each independently reversible with no
 * deploy: `enabled` alone does nothing but let the reconciler look at a school;
 * `+ dryRun` computes the full add/remove diff and writes NOTHING; dropping
 * `dryRun` makes the ADDS live; `+ removalsEnabled` makes the soft-REMOVALS live.
 * `removalsEnabled` is the only key that can take access away, which is why it is
 * separate from `enabled`.
 *
 * All three are REQUIRED on a write. The reconciler's resolver is fail-closed —
 * a missing key reads as `false` — so accepting a partial write would let an
 * operator flipping one rung silently clear the others.
 */
export const ParentMembershipReconcileValueSchema = z.object({
  enabled: z.boolean().describe('Master switch — false means the reconciler skips the school entirely'),
  removalsEnabled: z.boolean().describe('Soft-remove inference-owned rows. Meaningful only when `enabled`'),
  dryRun: z.boolean().describe('Compute the diff and write nothing. Meaningful only when `enabled`')
})

export type ParentMembershipReconcileValue = z.infer<typeof ParentMembershipReconcileValueSchema>

/**
 * Value shape of the `inbound_verification_sms` flag — the ALLOWLIST of phone
 * numbers the verification-code endpoint may read inbound 2FA texts from.
 *
 * WHY AN ALLOWLIST RATHER THAN ONE NUMBER. The endpoint takes the number as a
 * request parameter, so the automation can pass through whichever number the
 * account it is logging into uses — which is what makes this generalize beyond
 * our own Apple team to school-owned accounts. But `TwilioClient` holds
 * ACCOUNT-WIDE credentials, so an unconstrained parameter would let any holder of
 * the internal API key read any inbound SMS the Twilio account has ever
 * received, in one request. This row is the boundary: a number that is not on
 * this list cannot be read, whatever the caller asks for.
 *
 * `numbers` is REQUIRED and non-empty: writes replace the row's `value`
 * wholesale and the resolver is fail-closed, so an optional key would let a
 * partial write silently unconfigure the endpoint.
 *
 * E.164 (`+` followed by 7–15 digits, no spaces or punctuation) because that is
 * the form Twilio's `to` filter matches on. A number stored in any other format
 * matches nothing and reads as "no code has arrived yet" — indistinguishable
 * from the normal polling state, which is why the shape is enforced on write.
 */
export const InboundVerificationSmsValueSchema = z.object({
  numbers: z
    .array(
      z
        .string()
        .regex(/^\+[1-9]\d{6,14}$/, 'Must be an E.164 phone number, e.g. +17175550123')
        .describe('E.164 number the verification-code endpoint may read')
    )
    .min(1, 'At least one number must be allowlisted')
    .describe('Allowlist of E.164 numbers the verification-code endpoint may read inbound SMS from')
})

export type InboundVerificationSmsValue = z.infer<typeof InboundVerificationSmsValueSchema>

/**
 * Value shape of the `media_request_links` flag — the server-side master switch
 * for guest media request links (the public, unauthenticated "upload photos to
 * this collection" links minted from a media collection).
 *
 * WHY THIS FLAG EXISTS. The feature was fully reachable in production before it
 * had launched. The web dashboard gated it with a CLIENT-SIDE PostHog flag
 * (`media_library_upload_web`) whose own docstring says it is visibility only
 * and explicitly NOT an access-control boundary; the mobile app shipped the
 * whole list/create/revoke/copy-link surface with no gate at all; and the
 * backend had only AUTHORIZATION gates, which answer "may this caller do it?"
 * and never "is this feature on?". So there was no way to turn the feature off.
 * This row is that switch, and it covers web, mobile, and direct API calls at
 * once because it is enforced server-side.
 *
 * `enabled` is REQUIRED. Writes replace the row's `value` wholesale and the
 * resolver is fail-closed, so an optional key would let a partial write silently
 * unconfigure the switch.
 */
export const MediaRequestLinksValueSchema = z.object({
  enabled: z
    .boolean()
    .describe(
      'Master switch for guest media request links. False (or no row) means minting, updating, verifying and ' +
        'uploading through a request link are all refused; listing and revoking existing links stay available.'
    )
})

export type MediaRequestLinksValue = z.infer<typeof MediaRequestLinksValueSchema>

/**
 * Value shape of the `assignment_deletion_reconcile` flag — the rollout gate for
 * the per-section assignment DELETION reconciler (SCH-1050).
 *
 * WHY THIS FLAG EXISTS. The reconciler soft-deletes `AssignmentsOnSections` links
 * that Blackbaud no longer returns, from a write path shared by every school. Its
 * failure mode is the inverse of the bug it fixes: hiding live homework from
 * parents. The mitigation "watch the removed counts and stop the rollout" needs a
 * lever that exists, and a revert-and-deploy is slower than the fleet's next sync
 * cycle. This row is that lever, and it ships ABSENT — reconciliation is inert on
 * deploy until someone writes it.
 *
 * The two keys are a ladder, each independently reversible with no deploy:
 * `enabled` alone lets the sync consider reconciling a school at all;
 * `+ dryRun` computes the full diff, applies every guard, reports the count it
 * WOULD remove and writes nothing. Clearing `dryRun` makes the soft-deletes live.
 * A school is enabled only after its own `dryRun` pass has reported a plausible
 * number, because the "absence means deletion" premise was measured at one
 * section at one school and does not generalize for free.
 *
 * Both keys are REQUIRED. Writes replace the row's `value` wholesale and the
 * resolver is fail-closed, so accepting a partial write would let an operator
 * flipping one rung silently clear the other — here, clearing `dryRun` by
 * omission is exactly the mistake that turns a measurement into a deletion.
 */
export const AssignmentDeletionReconcileValueSchema = z.object({
  enabled: z
    .boolean()
    .describe('Master switch — false (or no row) means the per-section sync reconciles nothing for the school'),
  dryRun: z
    .boolean()
    .describe('Compute the diff and the would-remove count, write no `deletedAt`. Meaningful only when `enabled`')
})

export type AssignmentDeletionReconcileValue = z.infer<typeof AssignmentDeletionReconcileValueSchema>

/**
 * Master switch for the school icon library (Custom SVG Menu Icons plan,
 * KTD16). ONE flag gates BOTH authoring (upload/rename/replace/delete
 * through the library endpoints) and delivery (stored markup reaching a
 * device inside app config) — a school with a build that does not understand
 * the reference grammar must never see one authored, and the resolver's
 * absent-entry fallback (R14) is what keeps an already-authored reference
 * from breaking that school once the flag flips back off. Two separate
 * switches were considered and rejected: nobody could remember which one to
 * reach for during an incident. False (or no row) means: no library mutation
 * is accepted server-side (dashboard visibility is not the gate), and no
 * stored markup is embedded in delivered config, regardless of what the
 * dashboard shows. Defaults off in every environment.
 */
export const SchoolIconLibraryValueSchema = z.object({
  enabled: z
    .boolean()
    .describe(
      'Master switch for the school icon library. False (or no row) refuses every authoring endpoint ' +
        '(create/rename/replace/delete) server-side and omits stored markup from delivered app config.'
    )
})

export type SchoolIconLibraryValue = z.infer<typeof SchoolIconLibraryValueSchema>

/**
 * Beta allowlist for the student Planner (plan 2026-09-25-0756, KTD10). A
 * school is in the beta when its school-scoped row says `enabled: true`. The
 * backend checks it on the Planner feed read (and on every slice-2 write), so
 * the beta is enforced server-side and not only by the mobile tab gate. False
 * (or no row) means the Planner endpoints refuse the school with a 403.
 * Defaults off in every environment.
 */
export const StudentPlannerBetaValueSchema = z.object({
  enabled: z.boolean().describe('Planner beta switch. False (or no row) means the Planner endpoints refuse the school.')
})

export type StudentPlannerBetaValue = z.infer<typeof StudentPlannerBetaValueSchema>

/**
 * Read-time thresholds for the Version Ledger's drift and staleness flags
 * (plan 2026-09-02-2140, KTD12, R14).
 *
 * These do not gate a feature — they calibrate a judgment. The flags are
 * computed on every read from facts the sweep observed, and the only thing this
 * row decides is HOW MUCH silence counts as too much. That is exactly the kind
 * of number that has to be movable without a deploy: the sweep cadences are
 * operational and the fleet's OTA rhythm is a habit, not a constant.
 *
 * Every key is REQUIRED, per the registry rule: writes replace the row's value
 * wholesale, so an optional key would let an operator raising the stale window
 * silently unset the lane cadences beside it. The RESOLVER is nonetheless
 * tolerant of a missing key — an absent row, an unparseable value or a partial
 * one all fall back to the in-code defaults rather than suppressing every flag,
 * because a mis-typed threshold row must not silently blind the fleet table.
 *
 * `laneCadenceHours` keys the three lanes by their sweep, not by their provider
 * name: `expo` runs every 6 hours, `asc` and `play` daily. Multiplied by
 * `laneUnverifiedMultiplier`, that is 9 hours of Expo silence and 36 of store
 * silence before a lane reads as unverified.
 */
export const DeliveryStateThresholdsValueSchema = z.object({
  otaStaleDays: z
    .number()
    .positive()
    .describe('Days without a published OTA before an in-scope platform carries the `ota_stale` flag.'),
  laneUnverifiedMultiplier: z
    .number()
    .positive()
    .describe("Multiple of a lane's own sweep cadence it may go unchecked before it reads as unverified."),
  laneCadenceHours: z
    .object({
      expo: z.number().positive(),
      asc: z.number().positive(),
      play: z.number().positive()
    })
    .describe("Each lane's own sweep cadence in hours — the base the multiplier above is applied to.")
})

export type DeliveryStateThresholdsValue = z.infer<typeof DeliveryStateThresholdsValueSchema>

/**
 * Per-school gate for BATCHED section-roster fetching (SCH-1250, R16 / KTD6).
 *
 * When a school is enabled and an offering type is listed, the `SECTIONS_*`
 * parent stops emitting one `SECTION_ROSTER` job per section and instead emits
 * one job per CHUNK of sections; that job fetches every roster in the chunk with
 * a single `GET /{type}/rosters?section_ids=<csv>` call and then runs the
 * unchanged per-section write path for each. A school that is off, or an
 * offering type that is not listed, behaves exactly as it does today.
 *
 * ALL THREE KEYS ARE REQUIRED. Writes replace the row's `value` wholesale, so an
 * optional `offeringTypes` would let an operator flipping `enabled` silently
 * clear the type list — and an empty list is not the safe reading of that
 * mistake, it is an unreviewable one.
 *
 * `offeringTypes` IS THE REAL BLAST RADIUS, not `enabled`. The many-id
 * `section_ids` call was proven per offering type per school by the parity
 * harness; a type joins a school's list only after that harness returned a full,
 * untruncated response for it there. `DORMS` is in the enum because the fetch
 * path handles it, and stays off every list until a school with dorms is probed.
 *
 * `chunkSize` is an upper bound, not a promise. The resolver clamps it to 1..50
 * (at ~8 seconds a section, a chunk above ~52 spills against the job's
 * wall-clock budget by construction), and the emitter splits further whenever
 * the joined `section_ids` value would exceed Blackbaud's ~1,600-character
 * parameter cap — which at real 9-digit ids is the bound that binds first.
 *
 * Resolution is fail-closed: an absent row, a missing key, or a malformed value
 * all mean batching off for that school.
 */
export const SectionRosterBatchValueSchema = z.object({
  enabled: z
    .boolean()
    .describe(
      'Master switch for batched section-roster fetching at this school. False (or no row) makes every ' +
        'offering type emit one SECTION_ROSTER job per section, exactly as today.'
    ),
  chunkSize: z
    .number()
    .int()
    .min(1)
    .max(50)
    .describe(
      'Maximum sections per chunk job. Clamped to 1..50 by the resolver; the emitter splits a chunk further ' +
        'when the joined section_ids value would exceed the Blackbaud parameter-length cap.'
    ),
  offeringTypes: z
    .array(z.enum(['ACADEMIC', 'ACTIVITIES', 'ADVISORIES', 'ATHLETICS', 'DORMS', 'COMMUNITY_GROUPS']))
    .describe(
      'Offering types batching applies to at this school. A type absent from this list emits one job per ' +
        'section as today. Add a type only once the parity harness has returned a full response for it here.'
    )
})

export type SectionRosterBatchValue = z.infer<typeof SectionRosterBatchValueSchema>

/**
 * Per-school gate for the user-profile BULK WALK — the path that reads a
 * school's whole user population from Blackbaud in a handful of
 * `/users/extended` calls instead of one call per user (SCH-1249, KTD3).
 *
 * THREE MODES, NOT A BOOLEAN. `report` runs the walk and writes the walk report
 * (id-set differences plus a sampled field-parity check) while leaving the
 * school's sync behavior exactly as it is today, so a school is PROVEN
 * equivalent before it is switched. `on` replaces the per-user fan-out. `off`
 * is today's behavior and is what every unreadable state resolves to.
 *
 * ALL THREE KEYS ARE REQUIRED. A write replaces the row's `value` wholesale, so
 * an optional `chunkSize` would silently reset a school someone had tuned down
 * every time an operator flipped its mode — and an optional
 * `includeUserTokenPhase` would re-enable a phase that had been switched off to
 * keep a heavy school's children inside their reaper budget. Making the operator
 * restate all three is the point: the row is always fully specified.
 */
export const UserProfilesBulkWalkValueSchema = z.object({
  mode: z
    .enum(['off', 'report', 'on'])
    .describe(
      'off = today’s per-user fan-out; report = walk and write the walk report without changing sync behavior; ' +
        'on = the walk replaces the per-user fan-out for the legacy selection.'
    ),
  chunkSize: z
    .number()
    .int()
    .min(1)
    .max(250)
    .describe('Rows per SCHOOL_USER_PROFILES_PAGE child. 50 is the default; lower it at a heavy school.'),
  includeUserTokenPhase: z
    .boolean()
    .describe(
      'Whether a walked row still runs the user-token directory phase (Phase 2). True preserves today’s behavior; ' +
        'false drops ~85k /directories calls a week fleet-wide and is a separate decision from the walk itself.'
    )
})

export type UserProfilesBulkWalkValue = z.infer<typeof UserProfilesBulkWalkValueSchema>

/**
 * Value shape of the `background_jobs_force_one_shot` flag — the per-job-type
 * rollback lever for the light background-jobs lane (SCH-1653 U2, SCH-1748).
 *
 * WHY THIS FLAG EXISTS. Frequent light scheduled jobs moved from one-shot Fargate
 * tasks (`background-jobs-prod`, ~90 s of boot per ~3 s of work) to a consumer on
 * the always-on worker service (`background-jobs-light-prod`). If one of them
 * misbehaves in a long-lived process — a leak, a slow tick that crowds sync work —
 * naming it here sends it back to the one-shot queue on the next trigger, with no
 * deploy. Types not in the checked-in light lane are unaffected by this list.
 *
 * FLEET-WIDE ONLY: routing is not a per-school decision. An empty list (or no
 * row) is the checked-in routing.
 */
export const BackgroundJobsForceOneShotValueSchema = z.object({
  jobTypes: z
    .array(
      z
        .string()
        .regex(/^[A-Z][A-Z0-9_]*$/, 'Must be a background job type, e.g. STREAM_FOLLOW_SWEEPER')
        .describe('Background job type to send back to the one-shot queue')
    )
    .describe('Job types forced back to one-shot tasks. Empty means the checked-in routing.')
})

export type BackgroundJobsForceOneShotValue = z.infer<typeof BackgroundJobsForceOneShotValueSchema>

/**
 * The sweep's key in the fleet-wide `feature_health` flag row: how many schools
 * the thirty-minute feature-health sweep evaluates at once (plan 2026-09-12-2056,
 * KTD3, R35).
 *
 * The per-school work is replica and primary I/O, not CPU, so a small pool is
 * safe on the light lane. Read once per tick and clamped to 1..4; an absent key,
 * absent row or malformed value mean 2. The row's full value schema (with
 * `hooks_enabled`) composes this shape.
 */
export const FeatureHealthSweepConcurrencySchema = z.object({
  sweep_concurrency: z
    .number()
    .int()
    .describe('Schools the feature-health sweep evaluates at once. Clamped to 1..4; absent means 2.')
})

export type FeatureHealthSweepConcurrency = z.infer<typeof FeatureHealthSweepConcurrencySchema>

/**
 * Value shape of the whole `feature_health` flag row — school feature health's
 * runtime knobs (plan 2026-09-12-2056, KTD3, KTD6, R38). The registry's write gate.
 *
 * `hooks_enabled` is the kill switch for the event hooks. Two hooks ask for a
 * one-school recompute within a minute: a per-school feature switch changing,
 * and the school's Blackbaud integration going down. Both are best-effort; the
 * thirty-minute sweep grades every school regardless (R29). `false` stops both
 * on their next fire with no deploy. The daily role-coverage snapshot's
 * recompute is not governed here; its `ScheduledTask` row is its lever.
 *
 * `sweep_concurrency` is the sweep's key above, composed here; the write gate
 * refuses a value outside the 1..4 the reader would clamp it to.
 *
 * BOTH KEYS ARE REQUIRED, per the registry rule: a write replaces the row's
 * value wholesale, so an optional key would let an operator switching the hooks
 * off silently reset the sweep's concurrency (or the reverse). A write missing
 * either key is a 400. FLEET-WIDE ONLY. The readers still tolerate an absent
 * row, an absent key, a read error or a malformed value: the hooks stay ON — the
 * shipped behavior — because the lever exists to switch them off, and a config
 * read that blinks must not do it silently; the sweep uses 2.
 */
export const FeatureHealthFlagValueSchema = z.object({
  hooks_enabled: z
    .boolean()
    .describe(
      'Master switch for the feature-health event hooks (config change, integration down). False stops both; ' +
        'the thirty-minute sweep still grades every school.'
    ),
  sweep_concurrency: FeatureHealthSweepConcurrencySchema.shape.sweep_concurrency.min(1).max(4)
})

export type FeatureHealthFlagValue = z.infer<typeof FeatureHealthFlagValueSchema>

/**
 * Value shape of the `feature_health_coverage_account` flag — the per-school
 * fallback account the daily role-coverage snapshot probes instead of the
 * school's data-lookup account (plan 2026-09-12-2056, KTD8).
 *
 * Set by an operator only when the data-lookup account cannot be probed safely
 * at that school. The job never picks an account on its own: with no pointer and
 * no row here, the snapshot records "role coverage unavailable". `accountId` is a
 * `UsersOnSchools` public id (CUID), ownership-checked against the school before
 * any token read; `null` clears the fallback.
 *
 * SCHOOL-SCOPED: write it with `school_id`. The resolver reads the school's own
 * row only, so a fleet-wide row is ignored rather than aimed at every school.
 */
export const FeatureHealthCoverageAccountValueSchema = z.object({
  accountId: z
    .string()
    .min(1)
    .nullable()
    .describe(
      'Public id of the school account the role-coverage snapshot probes instead of the data-lookup account; ' +
        'null clears it.'
    )
})

export type FeatureHealthCoverageAccountValue = z.infer<typeof FeatureHealthCoverageAccountValueSchema>

/**
 * Value shape of the `apple_session_renewal` flag — the kill switch and the
 * safety caps for the server-side Apple (`apple-spaceship`) session renewal
 * (plan 2026-09-14-0916, KTD6).
 *
 * An ABSENT row means renewal is DISABLED: every claim is refused until an
 * operator writes this row. Every key is required because a write replaces the
 * value wholesale — an optional cap would let flipping the switch silently
 * clear the limits.
 *
 *   - `enabled`          the kill switch. `false` refuses every claim.
 *   - `maxSignInsPerDay` how many password submissions the shared Apple ID may
 *                        see per UTC day, counting in-flight reservations.
 *   - `maxSmsPerDay`     how many SMS codes may be requested per UTC day. Checked
 *                        at claim time too, because any password submission can
 *                        make Apple send one on its own.
 *   - `cooldownMinutes`  after a FAILED renewal, how long every trigger reports
 *                        `blocked` instead of signing in again.
 *   - `twilioAutoRead`   `true` lets the runner read Apple's code from the Twilio
 *                        relay; `false` makes it wait for a console-typed code only.
 *   - `appleSenders`     the pinned Apple SMS sender ids (KTD7). The verification
 *                        -code endpoint's `format=apple` mode reads this list on
 *                        the server and ignores any caller-supplied `from`. Empty
 *                        means no Twilio code can be attributed, so the runner
 *                        can only take a console-typed code.
 *
 * Suggested starting values: 12 sign-ins, 3 SMS, a 30-minute cooldown.
 */
export const AppleSessionRenewalValueSchema = z.object({
  enabled: z.boolean().describe('Kill switch — false (or no row) refuses every renewal claim'),
  maxSignInsPerDay: z
    .number()
    .int()
    .min(0)
    .max(100)
    .describe('Password submissions allowed per UTC day on the shared Apple ID, in-flight reservations included'),
  maxSmsPerDay: z
    .number()
    .int()
    .min(0)
    .max(50)
    .describe('SMS codes allowed per UTC day; also checked at claim time because Apple can send one unprompted'),
  cooldownMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60)
    .describe('Minutes after a FAILED renewal during which every trigger reports blocked instead of signing in'),
  twilioAutoRead: z
    .boolean()
    .describe('true: the runner reads the code from the Twilio relay; false: it waits for a console-typed code only'),
  appleSenders: z
    .array(z.string().min(1).max(32))
    .max(20)
    .describe('Pinned Apple SMS sender ids the verification-code endpoint accepts for format=apple')
})

export type AppleSessionRenewalValue = z.infer<typeof AppleSessionRenewalValueSchema>

/**
 * Value shape of the `collection_read_gate` flag — the rollout switch for the
 * album read gate (SCH-2109, R16/R17, KTD7).
 *
 * `GET /v1/collections/:id` and `/:id/media` check the signed-in viewer's access
 * to the album. In `log-only` a viewer with no access is logged and still
 * served; in `enforce` they get a 404. The gate ships at `log-only` and an
 * operator flips it after the denial log shows no wrong denials.
 *
 * `mode` is REQUIRED. The resolver treats an absent row, an absent key or any
 * other value as `log-only`, so an optional key would make a partial write
 * silently undo an `enforce`.
 */
export const CollectionReadGateValueSchema = z.object({
  mode: z
    .enum(['log-only', 'enforce'])
    .describe(
      'log-only = album reads by a viewer with no access are logged and still served; ' +
        'enforce = those reads get a 404. No row means log-only.'
    )
})

export type CollectionReadGateValue = z.infer<typeof CollectionReadGateValueSchema>

/**
 * Value shape of the `release_line_runtime_guard` flag — the switch for the
 * runtime guard on OTA and build dispatch (SCH-2198 U5, R25, KTD7).
 *
 * In `warn` the guard reports what it would skip or refuse and dispatches as
 * before; in `enforce` it skips and refuses. It ships at `warn` and is flipped
 * after the one-time runtime backfill, so existing apps are not refused as
 * `unknown`.
 *
 * `mode` is REQUIRED: the resolver treats an absent row, an absent key or any
 * other value as `warn`, so an optional key would let a partial write silently
 * switch the guard off.
 */
export const ReleaseLineRuntimeGuardValueSchema = z.object({
  mode: z
    .enum(['warn', 'enforce'])
    .describe(
      'warn = the guard reports the apps it would skip or refuse and the dispatch goes out as before; ' +
        'enforce = those apps are skipped or the dispatch is refused. No row means warn.'
    )
})

export type ReleaseLineRuntimeGuardValue = z.infer<typeof ReleaseLineRuntimeGuardValueSchema>

/**
 * Value shape of the `mobile_version_tag_writer` flag — the switch for the
 * backend's `mobile/v<version>` tag writer (SCH-2563 KTD4).
 *
 * On, the first successful production release of each marketing version from
 * `main` or a `release/mobile-*` branch tags its commit. It ships off and is
 * turned on only after the workflow tag step is removed from
 * `release/mobile-1.8` too, or that branch's old YAML tags first and the
 * Linear job never marks the version Released.
 *
 * `enabled` is REQUIRED: the resolver treats an absent row, an absent key or
 * any other value as off.
 */
export const MobileVersionTagWriterValueSchema = z.object({
  enabled: z
    .boolean()
    .describe('Version tag writer switch. False (or no row) means no release creates or confirms a mobile/v* tag.')
})

export type MobileVersionTagWriterValue = z.infer<typeof MobileVersionTagWriterValueSchema>

/**
 * Value shape of the `chat_role_table_mode` flag — the switch for the server's
 * who-can-start-with-whom check on chat creation and member adds (SCH-1288,
 * plan 2026-10-01-1411 KTD6).
 *
 * In `report` a recipient the school's `canInitiateChatWith` table does not
 * admit is logged and the chat goes ahead; in `enforce` the request is refused
 * with `CHAT_RECIPIENT_NOT_ALLOWED`. It ships at `report`; Ruben owns the flip.
 *
 * `mode` is REQUIRED: the resolver treats an absent row, an absent key or any
 * other value as `report`, so an optional key would let a partial write
 * silently undo an `enforce`.
 */
export const ChatRoleTableModeValueSchema = z.object({
  mode: z
    .enum(['report', 'enforce'])
    .describe(
      'report = a chat the role table would refuse is logged and still created; ' +
        'enforce = it is refused with CHAT_RECIPIENT_NOT_ALLOWED. No row means report.'
    )
})

export type ChatRoleTableModeValue = z.infer<typeof ChatRoleTableModeValueSchema>

/**
 * Value shape of the `smart_convert_v2` flag — the system-wide config row for
 * smart convert v2 on inbound email (SCH-2422, plan 2026-09-28-2100).
 *
 * It names the models, so a model change is a row edit, not a deploy.
 * `enabled: false` is the global kill switch, independent of each rule's
 * `enableSmartConvert`. `promptVersion` must equal the code's prompt version or
 * v2 stays off, so a prompt change cannot ship before the row is bumped after a
 * replay pass.
 *
 * Every key is REQUIRED, and the resolver parses the row with this same schema:
 * an absent row or any missing key means v2 is off, with no guessed default.
 */
export const SmartConvertV2ValueSchema = z.object({
  enabled: z.boolean().describe('Global kill switch. False (or no row) keeps today’s email cleaning for every school.'),
  textModel: z.string().min(1).describe('OpenRouter model id for the text classification pass.'),
  visionModel: z.string().min(1).describe('OpenRouter model id for the image (vision) pass.'),
  captionModel: z.string().min(1).describe('OpenRouter model id for image captions.'),
  timeoutMs: z.number().int().positive().describe('Per-call model timeout in milliseconds.'),
  promptVersion: z
    .string()
    .min(1)
    .describe('Must equal the deployed prompt version, or v2 stays off. Bump only after a replay pass.')
})

export type SmartConvertV2Value = z.infer<typeof SmartConvertV2ValueSchema>
